const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://www.thinkforwardic.com';

// Set <html lang>, the self-referencing canonical, and bidirectional hreflang
// alternates (en / zh-Hans / x-default) for the current page. `enPath` is the
// canonical English pathname (e.g. '/faq' or '/'); the zh variant is '/zh' + enPath.
export const setLanguageLinks = (lang: 'en' | 'zh', enPath: string) => {
  const enUrl = `${SITE_URL}${enPath}`;
  const zhUrl = enPath === '/' ? `${SITE_URL}/zh` : `${SITE_URL}/zh${enPath}`;
  const selfUrl = lang === 'zh' ? zhUrl : enUrl;

  document.documentElement.setAttribute('lang', lang === 'zh' ? 'zh-Hans' : 'en');

  const upsertLink = (rel: string, href: string, hreflang?: string) => {
    const sel = hreflang
      ? `link[rel="${rel}"][hreflang="${hreflang}"]`
      : `link[rel="${rel}"]:not([hreflang])`;
    let el = document.head.querySelector(sel) as HTMLLinkElement | null;
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', rel);
      if (hreflang) el.setAttribute('hreflang', hreflang);
      document.head.appendChild(el);
    }
    el.setAttribute('href', href);
  };

  upsertLink('canonical', selfUrl);
  upsertLink('alternate', enUrl, 'en');
  upsertLink('alternate', zhUrl, 'zh-Hans');
  upsertLink('alternate', enUrl, 'x-default');

  let ogLocale = document.head.querySelector('meta[property="og:locale"]') as HTMLMetaElement | null;
  if (!ogLocale) {
    ogLocale = document.createElement('meta');
    ogLocale.setAttribute('property', 'og:locale');
    document.head.appendChild(ogLocale);
  }
  ogLocale.setAttribute('content', lang === 'zh' ? 'zh_CN' : 'en_CA');
};

// SEO Helper - Dynamically inject Schema.org JSON-LD
export const injectSchemaOrg = (schema: object) => {
  // Remove existing schema if present
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  // Create and inject new schema
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(schema);
  document.head.appendChild(script);
};

// Update page meta tags dynamically
export const updatePageMeta = (meta: {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  if (meta.title) {
    document.title = meta.title;
    
    // Update OG title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', meta.title);
    
    // Update Twitter title
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (!twitterTitle) {
      twitterTitle = document.createElement('meta');
      twitterTitle.setAttribute('name', 'twitter:title');
      document.head.appendChild(twitterTitle);
    }
    twitterTitle.setAttribute('content', meta.title);
  }

  if (meta.description) {
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement('meta');
      desc.setAttribute('name', 'description');
      document.head.appendChild(desc);
    }
    desc.setAttribute('content', meta.description);
    
    // Update OG description
    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute('content', meta.description);
    
    // Update Twitter description
    let twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (!twitterDesc) {
      twitterDesc = document.createElement('meta');
      twitterDesc.setAttribute('name', 'twitter:description');
      document.head.appendChild(twitterDesc);
    }
    twitterDesc.setAttribute('content', meta.description);
  }

  if (meta.keywords) {
    let keywords = document.querySelector('meta[name="keywords"]');
    if (!keywords) {
      keywords = document.createElement('meta');
      keywords.setAttribute('name', 'keywords');
      document.head.appendChild(keywords);
    }
    keywords.setAttribute('content', meta.keywords);
  }

  if (meta.canonical) {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}${meta.canonical}`);
    
    // Update OG URL
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', `${siteUrl}${meta.canonical}`);
  }
};
